import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JobViewsMetrics } from "@/components/company/analytics/JobViewsMetrics";
import { CompanyJobViewsChart } from "@/components/company/analytics/CompanyJobViewsChart";
import { JobViewersTable } from "@/components/company/analytics/JobViewersTable";
import { TopJobsTable } from "@/components/company/analytics/TopJobsTable";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { subDays, format } from "date-fns";

export function AnalyticsTab() {
  const [dateRange] = useState({
    start: subDays(new Date(), 30),
    end: new Date(),
  });

  // Buscar analytics das vagas da empresa
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ["company-analytics", dateRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Buscar IDs das vagas da empresa
      const { data: jobs } = await supabase
        .from("jobs")
        .select("id, title")
        .eq("company_id", user.id);

      if (!jobs || jobs.length === 0) {
        return {
          totalViews: 0,
          uniqueViews: 0,
          totalClicks: 0,
          conversionRate: 0,
          dailyData: [],
          viewers: [],
          topJobs: [],
        };
      }

      const jobIds = jobs.map(j => j.id);

      // Total de views
      const { count: totalViews } = await supabase
        .from("job_views")
        .select("*", { count: "exact", head: true })
        .in("job_id", jobIds)
        .gte("viewed_at", dateRange.start.toISOString())
        .lte("viewed_at", dateRange.end.toISOString());

      // Views únicas (usuários únicos)
      const { data: uniqueViewsData } = await supabase
        .from("job_views")
        .select("user_id")
        .in("job_id", jobIds)
        .not("user_id", "is", null)
        .gte("viewed_at", dateRange.start.toISOString())
        .lte("viewed_at", dateRange.end.toISOString());

      const uniqueViews = new Set(uniqueViewsData?.map(v => v.user_id)).size;

      // Total de cliques em candidatar
      const { count: totalClicks } = await supabase
        .from("job_application_clicks")
        .select("*", { count: "exact", head: true })
        .in("job_id", jobIds)
        .gte("clicked_at", dateRange.start.toISOString())
        .lte("clicked_at", dateRange.end.toISOString());

      // Taxa de conversão
      const conversionRate = totalViews && totalViews > 0 
        ? ((totalClicks || 0) / totalViews) * 100 
        : 0;

      // Dados diários para o gráfico
      const { data: viewsData } = await supabase
        .from("job_views")
        .select("viewed_at")
        .in("job_id", jobIds)
        .gte("viewed_at", dateRange.start.toISOString())
        .lte("viewed_at", dateRange.end.toISOString())
        .order("viewed_at");

      const { data: clicksData } = await supabase
        .from("job_application_clicks")
        .select("clicked_at")
        .in("job_id", jobIds)
        .gte("clicked_at", dateRange.start.toISOString())
        .lte("clicked_at", dateRange.end.toISOString())
        .order("clicked_at");

      // Agrupar por dia
      const dailyMap = new Map<string, { views: number; clicks: number }>();
      
      viewsData?.forEach(v => {
        const day = format(new Date(v.viewed_at), "dd/MM");
        const current = dailyMap.get(day) || { views: 0, clicks: 0 };
        dailyMap.set(day, { ...current, views: current.views + 1 });
      });

      clicksData?.forEach(c => {
        const day = format(new Date(c.clicked_at), "dd/MM");
        const current = dailyMap.get(day) || { views: 0, clicks: 0 };
        dailyMap.set(day, { ...current, clicks: current.clicks + 1 });
      });

      const dailyData = Array.from(dailyMap.entries()).map(([date, data]) => ({
        date,
        ...data,
      }));

      // Buscar viewers (usuários que visualizaram)
      const { data: viewersData } = await supabase
        .from("job_views")
        .select(`
          user_id,
          viewed_at,
          profiles!inner(full_name, logo_url)
        `)
        .in("job_id", jobIds)
        .not("user_id", "is", null)
        .gte("viewed_at", dateRange.start.toISOString())
        .lte("viewed_at", dateRange.end.toISOString());

      // Agrupar viewers
      const viewersMap = new Map();
      viewersData?.forEach((view: any) => {
        const existing = viewersMap.get(view.user_id);
        if (existing) {
          existing.view_count++;
          if (new Date(view.viewed_at) > new Date(existing.last_view)) {
            existing.last_view = view.viewed_at;
          }
        } else {
          viewersMap.set(view.user_id, {
            user_id: view.user_id,
            full_name: view.profiles?.full_name,
            logo_url: view.profiles?.logo_url,
            viewed_at: view.viewed_at,
            view_count: 1,
            last_view: view.viewed_at,
          });
        }
      });

      const viewers = Array.from(viewersMap.values()).sort(
        (a, b) => b.view_count - a.view_count
      );

      // Top jobs por views
      const jobStatsPromises = jobs.map(async (job) => {
        const { count: views } = await supabase
          .from("job_views")
          .select("*", { count: "exact", head: true })
          .eq("job_id", job.id)
          .gte("viewed_at", dateRange.start.toISOString())
          .lte("viewed_at", dateRange.end.toISOString());

        const { count: clicks } = await supabase
          .from("job_application_clicks")
          .select("*", { count: "exact", head: true })
          .eq("job_id", job.id)
          .gte("clicked_at", dateRange.start.toISOString())
          .lte("clicked_at", dateRange.end.toISOString());

        return {
          id: job.id,
          title: job.title,
          views: views || 0,
          clicks: clicks || 0,
          conversion_rate: views && views > 0 ? ((clicks || 0) / views) * 100 : 0,
        };
      });

      const jobStats = await Promise.all(jobStatsPromises);
      const topJobs = jobStats.sort((a, b) => b.views - a.views);

      return {
        totalViews: totalViews || 0,
        uniqueViews,
        totalClicks: totalClicks || 0,
        conversionRate,
        dailyData,
        viewers,
        topJobs,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-8 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Não foi possível carregar os dados de analytics
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Analytics das Vagas</h2>
        <p className="text-muted-foreground">
          Acompanhe o desempenho das suas vagas nos últimos 30 dias
        </p>
      </div>

      <JobViewsMetrics
        totalViews={analyticsData.totalViews}
        uniqueViews={analyticsData.uniqueViews}
        totalClicks={analyticsData.totalClicks}
        conversionRate={analyticsData.conversionRate}
      />

      <CompanyJobViewsChart data={analyticsData.dailyData} />

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        <TopJobsTable jobs={analyticsData.topJobs} />
        <JobViewersTable viewers={analyticsData.viewers} />
      </div>
    </div>
  );
}
